import React from "react";
import { useNavigate } from "react-router-dom";
import { steps } from "./homeData/steps";
import { CTA_CHECKPOINTS, IconPlane } from "./homeData/homeConstants";

const allCards = [
  ...steps.map((s) => ({ title: s.title, desc: s.desc })),
  ...CTA_CHECKPOINTS.map((c) => ({ title: c.title, desc: c.desc })),
];

const TOTAL = allCards.length;

export default function CTABanner() {
  const navigate = useNavigate();
  const [progress,    setProgress]    = React.useState(0);
  const [launched,    setLaunched]    = React.useState(false);
  const [hovering,    setHovering]    = React.useState(false);
  const [pinned,      setPinned]      = React.useState(false);
  const [activeCard,  setActiveCard]  = React.useState(0);
  const [cardVisible, setCardVisible] = React.useState(true);

  const runwayRef  = React.useRef(null);
  const lastLitRef = React.useRef(-1);

  const LAUNCH_THRESHOLD = 88;

  const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

  const calcProgress = (clientX) => {
    const rw = runwayRef.current;
    if (!rw) return 0;
    const { left, width } = rw.getBoundingClientRect();
    const usable = width - 120;
    const raw = (clientX - left - 60) / usable * 100;
    return clamp(raw, 0, 100);
  };

  const litCount = React.useMemo(() => {
    let count = 0;
    allCards.forEach((_, i) => {
      const pos = (i / (TOTAL - 1)) * 100;
      if (progress >= pos) count++;
    });
    return count;
  }, [progress]);

  React.useEffect(() => {
    const newLit = litCount - 1;
    if (newLit >= 0 && newLit !== lastLitRef.current) {
      lastLitRef.current = newLit;
      setCardVisible(false);
      setTimeout(() => { setActiveCard(newLit); setCardVisible(true); }, 40);
    } else if (litCount === 0 && lastLitRef.current !== -1) {
      lastLitRef.current = -1;
      setActiveCard(0);
      setCardVisible(true);
    }
  }, [litCount]);

  const onRunwayMove = (e) => {
    if (launched) return;
    const p = calcProgress(e.clientX);
    setProgress(p);
    setHovering(true);
    if (p >= LAUNCH_THRESHOLD) triggerLaunch();
  };

  const onRunwayClick = () => {
    if (launched) return;
    setPinned((prev) => !prev);
  };

  const onRunwayLeave = () => {
    setHovering(false);
    if (pinned || launched) return;
    setProgress(0);
  };

  const onRunwayTouchMove = (e) => {
    if (launched) return;
    const p = calcProgress(e.touches[0].clientX);
    setProgress(p);
    setHovering(true);
    if (p >= LAUNCH_THRESHOLD) triggerLaunch();
  };

  const triggerLaunch = () => {
    setLaunched(true);
    setProgress(100);
    setTimeout(() => { navigate("/about"); }, 1400);
  };

  const lightClass = (i) => {
    const pos = (i / (TOTAL - 1)) * 100;
    const isLit = progress >= pos;
    if (!isLit) return "home-cta-runway__light";
    return `home-cta-runway__light ${progress > LAUNCH_THRESHOLD ? "home-cta-runway__light--green" : "home-cta-runway__light--yellow"}`;
  };

  const currentCard = activeCard >= 0 && activeCard < TOTAL ? allCards[activeCard] : null;

  const jumpToCard = (i) => {
    if (i === activeCard) return;
    setCardVisible(false);
    setTimeout(() => {
      setActiveCard(i);
      setProgress(Math.min((i / (TOTAL - 1)) * 100, LAUNCH_THRESHOLD - 1));
      setCardVisible(true);
    }, 40);
  };

  return (
    <section className="home-cta-banner">
      <div className="home-cta-banner__inner">
        <div className="home-cta-banner__head" data-aos="fade-right">
          <h2 className="home-cta-banner__title">Visa Assistance</h2>
          <p className="home-cta-banner__sub">
            No embassy visits. No paperwork confusion. Hover over the runway to explore everything we offer.
          </p>
        </div>

        <div className="home-cta-runway-wrap" data-aos="fade-left" data-aos-delay="150">

          <div className={`home-cta-runway${pinned ? " home-cta-runway--pinned" : ""}`} ref={runwayRef}
            onMouseMove={onRunwayMove}
            onMouseLeave={onRunwayLeave}
            onClick={onRunwayClick}
            onTouchMove={onRunwayTouchMove}
          >
            <div className="home-cta-runway__tarmac">
              <div className="home-cta-runway__dashes">
                {Array.from({ length: 12 }).map((_, i) => (
                  <div key={i} className="home-cta-runway__dash"
                    style={{ opacity: (i / 12) < (progress / 100) ? 0.12 : 0.4 }}
                  />
                ))}
              </div>
              <div className="home-cta-runway__fill" style={{ width: `${progress}%` }} />
              <div className="home-cta-runway__marker">
                <span className="home-cta-runway__marker-label">TAKEOFF</span>
              </div>
            </div>

            <div
              className={`home-cta-plane${hovering ? " home-cta-plane--hover" : ""}${pinned ? " home-cta-plane--pinned" : ""}${launched ? " home-cta-plane--launch" : ""}`}
              style={{ left: `calc(${progress}% - 60px)` }}
              onClick={() => navigate("/about")}
              aria-valuemin={0} aria-valuemax={100}
              aria-valuenow={Math.round(progress)}
              aria-label="Plane position indicator"
            >
              {progress > 12 && (
                <div className="home-cta-plane__trail">
                  {[0, 0.15, 0.3].map((delay, i) => (
                    <div key={i} className="home-cta-plane__trail-puff"
                      style={{ opacity: Math.min(progress / 80, 0.65) }}
                    />
                  ))}
                </div>
              )}
              <div className="home-cta-plane home-cta-plane__inner">
                <IconPlane />
              </div>
            </div>

            {launched && (
              <div className="home-cta-runway__launched">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
                  stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
                We're ready for your journey!
              </div>
            )}
          </div>

          <div className="home-cta-runway__lights">
            {allCards.map((_, i) => (
              <div key={i} className="home-cta-runway__light-wrap">
                <div className={lightClass(i)} />
              </div>
            ))}
          </div>

          {currentCard && (
            <div className="home-cta-info-card"
              style={{
                visibility: "visible",
                opacity: cardVisible ? 1 : 0,
                transform: cardVisible ? "translateY(0)" : "translateY(8px)",
                transition: "opacity .3s ease, transform .3s ease",
              }}
            >
              <div className="home-cta-info-card__body">
                <h3 className="home-cta-info-card__title home-how__card-title-combo">
                  {currentCard.title}
                </h3>
                <p className="home-cta-info-card__desc">{currentCard.desc}</p>
              </div>
            </div>
          )}

          {/* {!launched && (
            <div className="home-how__footer">
              <div className="home-how__dots">
                {allCards.map((_, i) => (
                  <button
                    key={i}
                    className={`home-how__dot${i === activeCard ? " home-how__dot--active" : ""}`}
                    onClick={() => jumpToCard(i)}
                    aria-label={`Go to card ${i + 1}`}
                  />
                ))}
              </div>
            </div>
          )} */}

        </div>
      </div>
    </section>
  );
}
